// Tenantra Inc. - Reset Password Page (Warm Sand Theme - Microsoft Style)
import { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Lock } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/services/api';

export default function ResetPasswordPage() {
  const [token, setToken] = useState('');
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'One number', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) }
  ];

  const isPasswordValid = passwordRequirements.every(req => req.test(password));
  const doPasswordsMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      setToken(urlToken);
      validateToken(urlToken);
    } else {
      setValidating(false);
      setTokenValid(false);
    }
  }, []);

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await api.post('/auth/verify-reset-token', null, {
        params: { token: tokenToValidate }
      });
      setTokenValid(response.data.valid);
      if (response.data.valid) {
        setMaskedEmail(response.data.email);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setTokenValid(false);
      setError('Invalid or expired reset link');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid || !doPasswordsMatch) return;
    
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col">
      <header className="h-16 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center">
          <Logo size="md" data-testid="auth-logo" />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-16">
        <div className="w-full max-w-[420px]">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 shadow-sm">
            {validating ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto mb-3" />
                <p className="text-sm text-stone-500">Validating reset link...</p>
              </div>
            ) : !tokenValid ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-stone-900 mb-2">Invalid Link</h2>
                <p className="text-sm text-stone-500 mb-4">
                  {error || 'This password reset link is invalid or has expired.'}
                </p>
                <a href="/forgot-password">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm h-9">
                    Request New Link
                  </Button>
                </a>
              </div>
            ) : success ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                </div>
                <h2 className="text-xl font-bold text-stone-900 mb-2">Password Reset!</h2>
                <p className="text-sm text-stone-500 mb-4">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
                <a href="/login">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm h-9">
                    Sign In
                  </Button>
                </a>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-stone-900 mb-1">New password</h2>
                  <p className="text-sm text-stone-600">For <strong className="text-stone-900">{maskedEmail}</strong></p>
                </div>

                {error && (
                  <Alert className="mb-4 bg-red-50 border-red-200 text-red-600 py-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-stone-700 text-sm">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-9 bg-white border-stone-200 text-stone-900 placeholder:text-stone-400 focus:border-teal-500 focus:ring-teal-500/20 pr-10 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    {password && (
                      <div className="grid grid-cols-2 gap-1.5 mt-2">
                        {passwordRequirements.map((req, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <div className={`w-3 h-3 rounded-full flex items-center justify-center ${
                              req.test(password) ? 'bg-teal-500' : 'bg-stone-200'
                            }`}>
                              {req.test(password) && <CheckCircle className="w-2 h-2 text-white" />}
                            </div>
                            <span className={`text-xs ${req.test(password) ? 'text-teal-600' : 'text-stone-400'}`}>
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-stone-700 text-sm">Confirm Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-9 bg-white border-stone-200 text-stone-900 placeholder:text-stone-400 focus:border-teal-500 focus:ring-teal-500/20 text-sm"
                    />
                    {confirmPassword && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {doPasswordsMatch ? (
                          <>
                            <CheckCircle className="w-3 h-3 text-teal-500" />
                            <span className="text-xs text-teal-600">Passwords match</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3 h-3 text-red-500" />
                            <span className="text-xs text-red-500">Passwords do not match</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-md shadow-teal-500/20 border-0 text-sm"
                    disabled={loading || !isPasswordValid || !doPasswordsMatch}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting...
                      </span>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Reset Password
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Tenantra Inc. ·{' '}
        <a href="/privacy-policy" className="hover:text-slate-700">Privacy</a> ·{' '}
        <a href="/terms-of-service" className="hover:text-slate-700">Terms</a>
      </footer>
    </div>
  );
}
